import Debug from 'debug';
import { BlHostError, ErrorCode } from './BlHostError';

import struct from "python-struct";
import events from "events";

const debug = Debug('blhost:cmd');

const LPC_BL_CMD_OUT_REPORT_ID = 0x1
const LPC_BL_DATA_OUT_REPORT_ID = 0x2
const LPC_BL_CMD_IN_REPORT_ID = 0x3
const LPC_BL_DATA_IN_REPORT_ID = 0x4

const BlHostCmdTag = {
    FlashEraseAll: 0x01,
    FlashEraseRegion: 0x02,
    ReadMemory: 0x03,
    WriteMemory: 0x04,
    FillMemory: 0x05,
    FlashSecurityDisable: 0x06,
    GetProperty: 0x07,
    ReceiveSbFile: 0x08,
    Execute: 0x09,
    Call: 0x0a,
    Reset: 0x0b,
    SetProperty: 0x0c,
    FlashEraseAllUnsecure: 0x0d,
    FlashProgramOnce: 0x0e,
    FlashReadOnce: 0x0f,
    FlashReadResource: 0x10,
    FlashReadResourceResponse: 0xb0,
    ConfigureQuadSpi: 0x11,
    ReliableUpdate: 0x12,
}

const BlHostRespTag = {
    GenericResponse: 0xa0,
    ReadMemoryResponse: 0xa3,
    GetPropertyResponse: 0xa7,
    FlashReadOnceResponse: 0xaf,
}


export const BlHostPropertyTag = {
    BootloaderVersion: 0x01,
    AvailablePeripherals: 0x02,
    FlashStartAddress: 0x03,
    FlashSizeInBytes: 0x04,
    FlashSectorSize: 0x05,
    FlashBlockCount: 0x06,
    AvailableCommands: 0x07,
    CrcCheckStatus: 0x08,
    Reserved9: 0x09,
    VerifyWrites: 0x0a,
    MaxPacketSize: 0x0b,
    ReservedRegions: 0x0c,
    Reserved13: 0x0d,
    RAMStartAddress: 0x0e,
    RAMSizeInBytes: 0x0f,
    SystemDeviceId: 0x10,
    FlashSecurityState: 0x11,
    UniqueDeviceId: 0x12,
    FacSupport: 0x13,
    FlashAccessSegmentSize: 0x14,
    FlashAccessSegmentCount: 0x15,
    FlashReadMargin: 0x16,
    QspiInitStatus: 0x17,
    TargetVersion: 0x18,
    ExternalMemoryAttributes: 0x19,
    ReliableUpdateStatus: 0x1a,
}




export const BlHostStatusCode = {
    // Generic statuses
    Success: 0,
    Fail: 1,
    ReadOnly: 2,
    OutOfRange: 3,
    InvalidArgument: 4,
    Timeout: 5,
    NoTransferInProgress: 6,

    // Flash driver errors
    FlashSizeError: 100,
    FlashAlignmentError: 101,
    FlashAddressError: 102,
    FlashAccessError: 103,
    FlashProtectionViolation: 104,
    FlashCommandFailure: 105,
    FlashUnknownProperty: 106,
    FlashEraseKeyError: 107,
    FlashRegionExecuteOnly: 108,
    FlashExecuteInRamFunctionNotReady: 109,

    // Memory interface errors
    MemoryRangeInvalid: 10200,
    MemoryReadFailed: 10201,
    MemoryWriteFailed: 10202,
    MemoryCumulativeWrite: 10203,
    MemoryAppOverlapWithExecuteOnlyRegion: 10204,
    MemoryAlignmentError: 10206,
    MemoryVerifyFailed: 10207,
    MemoryWriteProtected: 10208,
    MemoryBlankPageReadDisallowed: 10211,
    MemoryProtectedPageReadDisallowed: 10212,

    // Property store errors
    UnknownProperty: 10300,
    ReadOnlyProperty: 10301,
    InvalidPropertyValue: 10302,

    // Application crc check statuses
    AppCrcCheckPassed: 10400,
    AppCrcCheckFailed: 10401,
    AppCrcCheckInactive: 10402,
    AppCrcCheckInvalid: 10403,
    AppCrcCheckOutOfRange: 10404,

    // Reliable Update statuses
    ReliableUpdateSuccess: 10600,  // Reliable Update succeeded
    ReliableUpdateFail: 10601,  // Reliable Update failed
    ReliableUpdateInactive: 10602,  // Reliable Update Feature is inactive
    ReliableUpdateBackupApplicationInvalid: 10603,  // Backup Application is invalid
    ReliableUpdateStillInMainApplication: 10604,  // Next boot will be still in Main Application
    ReliableUpdateSwapSystemNotReady: 10605,  // Cannot swap flash by default because swap system is not ready
    ReliableUpdateBackupBootloaderNotReady: 10606,  // Cannot swap flash because there is no valid backup bootloader
    ReliableUpdateSwapIndicatorAddressInvalid: 10607,  // Cannot swap flash because provided swap indicator is invalid
}




/**
 * BlHostCommand class
 *
 * Implements sending commands and parsing their responses.
 *
 */
export default class BlHostCommands {
    constructor(blHostTransport) {
        this.transport = blHostTransport;
        this.events = new events.EventEmitter();
    }

    assertResponseTag(expectedCommandTag, comment) { // eslint-disable-line class-methods-use-this
        console.log('returning function: assertResponseTag', expectedCommandTag, comment);

        return (response) => {
            console.log('assertResponseTag', expectedCommandTag, comment);

            if (!response) {
                debug('Tried to assert an empty parsed response!');
                debug('response: ', response);
                throw new BlHostError(ErrorCode.ERROR_ASSERT_EMPTY_RESPONSE);
            }
            const [commandTag, bytes] = response;

            if (commandTag !== expectedCommandTag) {
                throw new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_COMMANDTAG, `Expected commandTag ${expectedCommandTag}, got ${commandTag} instead.`);
            }

            return bytes;
        };
    }

    assertResponseLength(expectedLength) { // eslint-disable-line class-methods-use-this
        return responseParams => {
            if (!responseParams) {
                debug('Tried to assert an empty parsed responseParams!');
                debug('responseParams: ', responseParams);
                throw new BlHostError(ErrorCode.ERROR_ASSERT_EMPTY_RESPONSE);
            }

            if (responseParams.length !== expectedLength) {
                throw new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES, `Expected ${expectedLength} bytes in responseParams, got ${responseParams.length} bytes instead.`);
            }

            return responseParams;
        };
    }

    assertResponseStatusCode(expectedStatusCode) { // eslint-disable-line class-methods-use-this
        return responseParams => {
            if (!responseParams) {
                debug('Tried to assert an empty parsed responseParams!');
                throw new BlHostError(ErrorCode.ERROR_ASSERT_EMPTY_RESPONSE);
            }

            if (responseParams.statusCode !== expectedStatusCode) {
                throw new BlHostError(responseParams.statusCode,
                    `Expected statusCode ${expectedStatusCode.toString(16)}, got ${responseParams.statusCode.toString(16)} instead.`);
            }

            return responseParams;
        };
    }

    readUntilAllBytesReceived(expectedLength) {
        return async () => {
            // we need to continue doing this until we received 'expectedLength' bytes
            while (this.rxCount < expectedLength) {
                let [reportId, data] = await this.transport.read.bind(this.transport)();
                if (reportId !== LPC_BL_DATA_IN_REPORT_ID) {
                    throw new BlHostError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES,
                        `Expected response on reportId ${LPC_BL_DATA_IN_REPORT_ID}, got ${reportId} instead.`);
                }
                this.rxBytes.set(data, this.rxCount);
                this.rxCount += data.length;
                debug("received ", data.length, " data bytes. ", this.rxCount, "/", expectedLength);

                if (data.length == 0) {
                    // There is an error reading data; the reason will be given in the GenericResponse
                    debug("WARNING: data length 0 received; bailing out.")
                    this.rxCount = expectedLength;
                }
            }

            // Read the next response, which should be the Final GenericResponse
            return this.transport.read.bind(this.transport)();
        }
    }

    // Note that the payload size should be round up to a multiple of the FlashSectorSize
    writeUntilAllBytesSent(payload, maxPacketSize) {
        return async () => {
            // we need to continue doing this until we received 'expectedLength' bytes
            this.txCount = 0;
            const expectedLength = payload.length;

            while (this.txCount < expectedLength) {
                // Clip to maxPacketSize
                const maxLength = Math.min(payload.length - this.txCount, maxPacketSize);
                const nextChunk = payload.subarray(this.txCount, this.txCount + maxLength);
                await this.transport.sendData(nextChunk);
                this.txCount += nextChunk.length;
                debug("sent ", nextChunk.length, " data bytes. ", this.txCount, "/", expectedLength, ":", this.txCount * 100 / expectedLength, "%");
                this.events.emit('writeMemoryUpdate', { done: false, current: this.txCount, total: expectedLength });
            }

            // Read the next response, which should be the Final GenericResponse
            this.events.emit('writeMemoryUpdate', { done: true, current: this.txCount, total: expectedLength });
            return this.transport.read.bind(this.transport)();
        }
    }

    // Note that the payload size should be round up to a multiple of the FlashSectorSize
    writeUntilAllBytesSent_Promise(payload, maxPacketSize, init=true) {
        // we need to continue doing this until we received 'expectedLength' bytes
        if (init) {
            this.txCount = 0;
        }
        const expectedLength = payload.length;

        // return () => {
            const maxLength = Math.min(payload.length - this.txCount, maxPacketSize);
            const nextChunk = payload.subarray(this.txCount, this.txCount + maxLength);
            this.transport.sendData.bind(this.transport)(nextChunk).then(() => {
                this.txCount += nextChunk.length;
                debug("sent ", nextChunk.length, " data bytes. ", this.txCount, "/", expectedLength, ":", this.txCount * 100 / expectedLength, "%");
                if (this.txCount === expectedLength) {
                    this.events.emit('writeMemoryUpdate', { done: true, current: this.txCount, total: expectedLength });
                    // Read the next response, which should be the final GenericResponse
                    return this.transport.read.bind(this.transport)();
                } else {
                    this.events.emit('writeMemoryUpdate', { done: false, current: this.txCount, total: expectedLength });
                    // Call this method again; it shouldn't resolve yet, just keep going
                    this.writeUntilAllBytesSent(payload, maxPacketSize, false);
                }
            });
        // };
    }

    getProperty(propertyId) {
        debug('getProperty: ', propertyId);

        return this.sendCommandGetProperty(propertyId)
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.GetPropertyResponse, 'getProperty'))
            .then(this.assertResponseLength(4 * 2)) // status + property value
            .then((response) => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    status: dataView.getInt32(0, true),
                    propertyValue: dataView.getInt32(4, true),
                };
            })
            .then(property => {
                debug('getProperty status: ', property.status.toString(16));
                debug('getProperty value:  ', property.propertyValue.toString(16));
                return property;
            });
    }

    flashEraseAll() {
        debug('flashEraseAll');

        return this.sendCommandFlashEraseAll()
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.GenericResponse, 'flashEraseAll'))
            .then(this.assertResponseLength(4 * 2)) // statusCode + commandTag
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    commandTag: dataView.getInt32(4, true),
                };
            })
            .then(genericResponse => {
                debug('flashEraseAll statusCode: ', genericResponse.statusCode.toString(16));
                debug('flashEraseAll commandTag: ', genericResponse.commandTag.toString(16));
                return genericResponse;
            });
    }

    flashEraseRegion(startAddress, byteCount, memoryId = 0) {
        debug('flashEraseAll');

        return this.sendCommandFlashEraseRegion(startAddress, byteCount, memoryId)
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.GenericResponse, 'flashEraseRegion'))
            .then(this.assertResponseLength(4 * 2)) // statusCode + commandTag
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    commandTag: dataView.getInt32(4, true),
                };
            })
            .then(genericResponse => {
                debug('flashEraseRegion statusCode: ', genericResponse.statusCode.toString(16));
                debug('flashEraseRegion commandTag: ', genericResponse.commandTag.toString(16));
                return genericResponse;
            });
    }

    readMemory(startAddress, length, memoryId = 0) {
        debug('readMemory');

        return this.sendCommandReadMemory(startAddress, length, memoryId)
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.ReadMemoryResponse, 'readMemory'))
            .then(this.assertResponseLength(4 * 2)) // statusCode + dataByteCount
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    dataByteCount: dataView.getInt32(4, true),
                };
            })
            .then(this.assertResponseStatusCode(BlHostStatusCode.Success))
            .then(response => {
                debug('readMemory statusCode:    ', response.statusCode.toString(16));
                debug('readMemory dataByteCount: ', response.dataByteCount.toString(10));
                this.rxCount = 0;
                this.rxBytes = new Uint8Array(length);
                return response;
            })
            .then(this.readUntilAllBytesReceived(length))
            .then(this.assertResponseTag(BlHostRespTag.GenericResponse, 'readUntilAllBytesReceived'))
            .then(this.assertResponseLength(8)) // statusCode + commandTag
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    commandTag: dataView.getInt32(4, true),
                };
            })
            .then(this.assertResponseStatusCode(BlHostStatusCode.Success))
            .then(genericResponse => {
                debug('readMemory statusCode: ', genericResponse.statusCode.toString(16));
                debug('readMemory commandTag: ', genericResponse.commandTag.toString(16));
                return [genericResponse, this.rxBytes]; //genericResponse;
            });
    }

    // Prepare a flashBlob from a binFile byte array.
    // This will correctly round up the binary to align with flashSectorSize
    getFlashBlobFromBinFile(binFileBytes, flashSectorSize) {
        const binSizeRounded = (Math.floor((binFileBytes.length + flashSectorSize - 1) / flashSectorSize))
            * flashSectorSize;
        debug("binSize:", binFileBytes, "binSizeRounded:", binSizeRounded, "flashSectorSize:", flashSectorSize);
        const flashBlob = new Uint8Array(binSizeRounded);
        flashBlob.set(binFileBytes, 0);
        for (let i = binFileBytes.length; i < binSizeRounded; i++) {
            flashBlob[i] = 0xFF;
        }
        return flashBlob;
    }

    async writeMemory(startAddress, flashBlob, maxPacketSize, memoryId = 0) {
        debug('writeMemory');

        const result = this.sendCommandWriteMemory(startAddress, flashBlob.length, memoryId)
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.GenericResponse, 'sendCommandWriteMemory'))
            .then(this.assertResponseLength(4 * 2)) // statusCode + dataByteCount
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    dataByteCount: dataView.getInt32(4, true),
                };
            })
            .then(this.assertResponseStatusCode(BlHostStatusCode.Success))
            .then(response => {
                debug('writeMemory statusCode:    ', response.statusCode.toString(16));
                debug('writeMemory dataByteCount: ', response.dataByteCount.toString(10));
                return response;
            })
            .then(this.writeUntilAllBytesSent(flashBlob, maxPacketSize));

        await result;
        result.then(
                this.assertResponseTag(BlHostRespTag.GenericResponse, 'writeUntilAllBytesSent')
            )
            .then(this.assertResponseLength(8)) // statusCode + commandTag
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    commandTag: dataView.getInt32(4, true),
                };
            })
            .then(this.assertResponseStatusCode(BlHostStatusCode.Success))
            .then(genericResponse => {
                debug('readMemory statusCode: ', genericResponse.statusCode.toString(16));
                debug('readMemory commandTag: ', genericResponse.commandTag.toString(16));
                return [genericResponse, this.rxBytes]; //genericResponse;
            });
    }

    reset() {
        debug('reset');

        return this.sendCommandReset()
            .then(this.transport.read.bind(this.transport))
            .then(this.assertResponseTag(BlHostRespTag.GenericResponse, 'reset'))
            .then(this.assertResponseLength(8)) // statusCode + commandTag
            .then(response => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset as the Uint8Array for the packet payload
                const dataView = new DataView(response.buffer, response.byteOffset);
                return {
                    statusCode: dataView.getInt32(0, true),
                    commandTag: dataView.getInt32(4, true),
                };
            })
            .then(this.assertResponseStatusCode(BlHostStatusCode.Success))
            .then(genericResponse => {
                debug('reset statusCode: ', genericResponse.statusCode.toString(16));
                debug('reset commandTag: ', genericResponse.commandTag.toString(16));
                return genericResponse;
            });
    }

    sendCommandGetProperty(propertyId) {
        const paramList = new Uint8Array(4 * 2);
        paramList.set(struct.pack("<I", propertyId), 4 * 0);
        paramList.set(struct.pack("<I", 0), 4 * 1);
        return this.transport.sendCommand(BlHostCmdTag.GetProperty, paramList);
    }

    sendCommandReadMemory(startAddress, byteCount, memoryId) {
        const paramList = new Uint8Array(3 * 4);
        paramList.set(struct.pack("<I", startAddress), 4 * 0);
        paramList.set(struct.pack("<I", byteCount), 4 * 1);
        paramList.set(struct.pack("<I", memoryId), 4 * 2);
        return this.transport.sendCommand(BlHostCmdTag.ReadMemory, paramList);
    }

    sendCommandWriteMemory(startAddress, byteCount, memoryId = 0) {
        const paramList = new Uint8Array(4 * 3);
        paramList.set(struct.pack("<I", startAddress), 4 * 0);
        paramList.set(struct.pack("<I", byteCount), 4 * 1);
        paramList.set(struct.pack("<I", memoryId), 4 * 2);
        return this.transport.sendCommand(BlHostCmdTag.WriteMemory, paramList);
    }

    sendCommandFlashEraseAll(memoryId = 0) {
        const paramList = new Uint8Array(4 * 1);
        paramList.set(struct.pack("<I", memoryId), 4 * 0);
        return this.transport.sendCommand(BlHostCmdTag.FlashEraseAll, paramList);
    }

    sendCommandFlashEraseRegion(startAddress, byteCount, memoryId) {
        const paramList = new Uint8Array(4 * 3);
        paramList.set(struct.pack("<I", startAddress), 4 * 0);
        paramList.set(struct.pack("<I", byteCount), 4 * 1);
        paramList.set(struct.pack("<I", memoryId), 4 * 2);
        return this.transport.sendCommand(BlHostCmdTag.FlashEraseAll, paramList);
    }

    sendCommandReset() {
        const paramList = new Uint8Array(4 * 0);
        return this.transport.sendCommand(BlHostCmdTag.Reset, paramList);
    }
}
